import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { useFormikContext } from 'formik'
import ReportForm from './ReportForm'
import { InvoiceTemplateDto } from '../../../../config/finance/invoiceConfig'
import useEntity from '../../../../hooks/useEntity'
import Loading from '../../../../components/Loading'
import { DocumentGenerationType } from '../../../../hooks/document/useDocumentGenerationHandler'
import { getTemplateInterval } from '../Invoice/InvoiceFile'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import DateRange from '../../../DatePicker/DateRange'
import FormikField from '../../../Formik/FormikField'
import UserPicker, { UserPickerProps } from '../../../TagPicker/UserPicker'
import { Switch, SwitchOnChangeData, SwitchProps } from '@fluentui/react-components'
import { ReportFormType } from '../../../../pages/Finance/Reports'
import FormikComboboxField from '../../../Formik/FormikComboboxField'
import FilePicker, { FilePickerProps } from '../../../TagPicker/FilePicker'

export type TimeReportDto = {
    from: string
    to: string
    files: string[]
    fileOwners: string[]
    itemUsers: string[]
    salesRate: boolean
}

const getValidationSchema = (translate: (key: string) => string): Yup.ObjectSchema<TimeReportDto> => {
    return Yup.object().shape({
        from: Yup.string().label(translate('base.daterange')).required(),
        to: Yup.string().label(translate('base.daterange')).required(),
        files: Yup.array().label(translate('time.filter.file')).required(),
        fileOwners: Yup.array().label(translate('file.filter.owner')).required(),
        itemUsers: Yup.array().label(translate('file.filter.executive')).required(),
        salesRate: Yup.boolean().label(translate('file.edit.form.finance.rates.tier.salesRate')).required(),
    })
}

function ReportBody(props: ReportFormType): ReactElement {
    const { t: translate } = useTranslation()
    const { errors, values, setFieldValue } = useFormikContext<TimeReportDto>()

    return (
        <>
            <DateRange
                startValue={values.from}
                endValue={values.to}
                changeStartDate={date => setFieldValue('from', date)}
                changeEndDate={date => setFieldValue('to', date)}
                orientation={props.orientation}
                required
                showLabel
                validationMessage={errors.from || errors.to}
            />
            <FormikComboboxField<TimeReportDto, FilePickerProps>
                orientation={props.orientation}
                labelKey="time.filter.file"
                fieldName={'files'}
                component={FilePicker}
            />
            <FormikComboboxField<TimeReportDto, UserPickerProps>
                orientation={props.orientation}
                labelKey="file.filter.owner"
                fieldName={'fileOwners'}
                component={UserPicker}
            />
            <FormikComboboxField<TimeReportDto, UserPickerProps>
                orientation={props.orientation}
                labelKey="file.filter.executive"
                fieldName={'itemUsers'}
                component={UserPicker}
            />
            <FormikField<SwitchProps, TimeReportDto>
                orientation={props.orientation}
                labelKey="file.edit.form.finance.rates.tier.salesrate"
                fieldName={'salesRate'}
                component={Switch}
                componentProps={{
                    checked: values.salesRate,
                    label: values.salesRate ? translate('base.toggle.boolean.true') : translate('base.toggle.boolean.false'),
                    labelPosition: 'after',
                    onChange: (_, data: SwitchOnChangeData) => {
                        setFieldValue('salesRate', data.checked)
                    },
                }}
            />
        </>
    )
}

export default function TimeReportForm(props: ReportFormType): ReactElement {
    const { t: translate } = useTranslation()

    const [templates] = useEntity<InvoiceTemplateDto[]>(() => ({ url: '/finance/invoice/template' }), false)
    const interval = useMemo(() => getTemplateInterval(templates?.data || []), [templates])

    if (templates.loading) {
        return <Loading label="Bezig met laden declaratietypes..." />
    }

    return (
        <ReportForm<TimeReportDto>
            title="Declaratieadvies"
            generationType={DocumentGenerationType.OutstandingCharges}
            initialValues={{
                from: interval.start.toISODate(),
                to: interval.end.toISODate(),
                files: [],
                fileOwners: [],
                itemUsers: [],
                salesRate: true,
            }}
            validationSchema={getValidationSchema(translate)}
            {...props}
        >
            <ReportBody {...props} />
        </ReportForm>
    )
}
